import { inject } from 'inversify';

import { UserController } from '@/controllers/UserController';
import { service } from '@/decorators/service';
import { SignalrListener, SignalrMessage } from '@/decorators/signalr';
import { Logger } from '@/services/Logger';
import { NotificationUserUpdateReqMsg } from '@/services/signalr/MessageTypes/Notification';
import { Signalr } from '@/services/signalr/Signalr';
import { NotificationStore } from '@/stores/NotificationStore';
import { INotificationUserInfo } from '~/api/INotification';
import { IDispatchMessage } from '~/api/messages/IDispatchMessage';
import { INotificationRespMsg } from '~/api/messages/INotificationRespMsg';
import { INotificationUserUpdateRespMsg } from '~/api/messages/INotificationUserUpdateRespMsg';

@SignalrListener
@service(NotificationManager)
export class NotificationManager {
  @inject(Logger) protected logger: Logger;
  @inject(Signalr) protected signalr: Signalr;
  @inject(UserController) protected userController: UserController;
  @inject(NotificationStore) protected notificationStore: NotificationStore;

  @SignalrMessage('NotificationRespMsg')
  async onNotificationRespMsg(response: INotificationRespMsg & IDispatchMessage) {
    const notifications = response.notificationInfos
      .filter((n) => n.userKey !== this.userController.activeUser.userName);

    if (notifications.length > 0) {
      this.notificationStore.batchUpdate(notifications);
    }
  }

  @SignalrMessage('NotificationUserRespMsg')
  async onNotificationUserRespMsg(response: INotificationUserUpdateRespMsg & IDispatchMessage) {
    const notificationUserInfoList = response.notificationUserInfoList as INotificationUserInfo[] | undefined;
    if (notificationUserInfoList && notificationUserInfoList.length > 0) {
      const currentUserKey = this.userController.activeUser.userKey;
      const userInfo = notificationUserInfoList.find((info: INotificationUserInfo) => info.userKey === currentUserKey);
      if (userInfo) {
        this.notificationStore.updateUserInfo(userInfo);
      }
    }
  }

  async acknowledge(notificationId: string) {
    await this.signalr.invoke(NotificationUserUpdateReqMsg, {
      notificationUserInfo: {
        notificationIds: [notificationId],
        userKey: this.userController.activeUser.userKey,
      },
    });
  }

  async acknowledgeAll() {
    const notifications = this.notificationStore.all();
    if (notifications.length === 0) return;

    const readNotifications = this.getAcknowledgedNotifications.value;
    const notificationIds = notifications.filter((n) => !readNotifications?.includes(n.notificationId))
      .map((n) => n.notificationId);
    await this.signalr.invoke(NotificationUserUpdateReqMsg, {
      notificationUserInfo: {
        notificationIds,
        userKey: this.userController.activeUser.userKey,
      },
    });
  }

  get getAcknowledgedNotifications() {
    return this.notificationStore.notificationIds;
  }

  get isAcknowledged() {
    return (notificationId: string) => this.notificationStore.notificationIds.value.includes(notificationId);
  }

  get getNotifications() {
    return this.notificationStore.all();
  }

  getNotification(notificationId: string) {
    return this.notificationStore.find(notificationId);
  }
}
